import { useNode } from "@craftjs/core";
import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import {
  goToUrl,
  percentageToSlider,
  sliderToPercentage,
} from "../../../../utils/helpers";
import CustomSwitch from "../../../CustomSwitch";
import Input from "../../../Input";
import Tabs from "../../../Tabs";
import TextView from "../../../TextView";
import ColorPopup from "../../../SegmentBuilder/Shared/ColorPopup";
import MarginBox from "../../Shared/MarginBox";
import SettingsSlider from "../../Shared/SettingsSlider";
import EmbedURL from "../../../SegmentBuilder/Shared/EmbedURL";
import UploadedMedia from "../../Shared/UploadedMedia";
import UploadMedia from "../../Shared/UploadMedia";
import MediaBrowser from "../../../SegmentBuilder/Shared/MediaBrowser";
import ShareWithBorder from "../../../../assets/icons/ShareWithBorder";
import RecentMedia from "../../Shared/RecentMedia";
import { useDispatch, useSelector } from "react-redux";
import { updateVideoStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import { StyledTooltip } from "../../../IyugaTooltip";
import EssentialPackageIcon from "@assets/images/packages/essentialPackageIcon.svg";

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.lightBorder};
  margin-bottom: 20px;
`;

const VideoSettings = () => {
  const {
    actions: { setProp },
    width,
    isApplyMarginOnAllSides,
    margin,
    allSideMargin,
    borderRadius,
    borderColor,
    borderWidth,
    src,
    title,
    showVideoTitle,
    hyperlink,
    showHyperlink,
    dropShadowColor,
  } = useNode((node) => ({
    width: node.data.props.width,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    borderRadius: node.data.props.borderRadius,
    borderWidth: node.data.props.borderWidth,
    borderColor: node.data.props.borderColor,
    src: node.data.props.src,
    title: node.data.props.title,
    showVideoTitle: node.data.props.showVideoTitle,
    hyperlink: node.data.props.hyperlink,
    showHyperlink: node.data.props.showHyperlink,
    dropShadowColor: node.data.props.dropShadowColor,
    isLoading: node.data.props.isLoading,
  }));

  const dispatch = useDispatch();
  const { video: videoProps } = useSelector((state) => state.codeblockStyles);

  const mainTabs = ["Video Selection", "Video Layout"];
  const [selectedTab, setSelectedTab] = useState(mainTabs[0]);
  const categories = ["Upload", "Embed URL", "Browse", "Recent"];
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.UPLOAD_VIDEOS);

  const categoriesWithIcons = categories.map((category) => {
    const config = {
      label: category,
      value: category,
    };

    // Add tooltip only to Image category when not eligible
    if (category === "Upload" && !isEligible) {
      config.customIcon = (
        <StyledTooltip
          title={"Essential Tier feature"}
          arrow
          placement="bottom"
          color={"#325AE9"}
          textColor={"#fff"}
        >
          <img src={EssentialPackageIcon} alt="info" />
        </StyledTooltip>
      );
    }

    return config;
  });

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  // Update default props in reducer when settings change
  const updateDefaultVideoProps = (updatedProps) => {
    const updatedVideoProps = {
      ...videoProps,
      ...updatedProps,
    };
    dispatch(updateVideoStyles(updatedVideoProps));
  };

  const handleChange = (type, value, side) => {
    setProp((props) => {
      switch (type) {
        case "width":
          props.width = sliderToPercentage(value);
          updateDefaultVideoProps({ width: sliderToPercentage(value) });
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
            const updatedMargin = { ...props.margin };
            updateDefaultVideoProps({ margin: updatedMargin });
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          updateDefaultVideoProps({ allSideMargin: value });
          break;
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          updateDefaultVideoProps({
            isApplyMarginOnAllSides: !props.isApplyMarginOnAllSides,
          });
          break;
        case "border":
          props.borderRadius = value;
          updateDefaultVideoProps({ borderRadius: value });
          break;
        case "borderColor":
          props.borderColor = value;
          updateDefaultVideoProps({ borderColor: value });
          break;
        case "resetColor":
          props.borderColor = null;
          updateDefaultVideoProps({ borderColor: null });
          break;
        case "borderWidth":
          props.borderWidth = value;
          updateDefaultVideoProps({ borderWidth: value });
          break;
        case "video":
          props.src = value.src;
          props.thumbnail = value.thumbnail;
          // Set loading state based on the uploaded file data only if loading key exists
          if (value.hasOwnProperty("loading")) {
            props.isLoading = value.loading !== false;
          }
          break;
        case "title":
          props.title = value;
          updateDefaultVideoProps({ title: value });
          break;
        case "showVideoTitle":
          props.showVideoTitle = !props.showVideoTitle;
          updateDefaultVideoProps({ showVideoTitle: !props.showVideoTitle });
          break;
        case "showHyperlink":
          props.showHyperlink = !props.showHyperlink;
          updateDefaultVideoProps({ showHyperlink: !props.showHyperlink });
          break;
        case "hyperlink":
          props.hyperlink = value;
          updateDefaultVideoProps({ hyperlink: value });
          break;
        case "dropShadowColor":
          props.dropShadowColor = value;
          updateDefaultVideoProps({ dropShadowColor: value });
          break;
        case "resetShadow":
          props.dropShadowColor = null;
          updateDefaultVideoProps({ dropShadowColor: null });
          break;
        case "videoRemove":
          props.src = "";
          props.thumbnail = "";
      }
    }, 500);
  };

  const videoSelected = src && src !== ASSETS.segmentEditor.UploadVideo;

  const renderVideoSelection = () => {
    return (
      <>
        {videoSelected && (
          <UploadedMedia
            type="video"
            media={src}
            title={title}
            onChange={(val) => handleChange("title", val)}
            showTitle={showVideoTitle}
            handleCheckbox={() => handleChange("showVideoTitle")}
            handleEditMedia={(value) => handleChange("video", value)}
            handleMediaRemove={() => handleChange("videoRemove")}
          />
        )}

        <Tabs
          categoriesWithIcons={categoriesWithIcons}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          tabStyle={{ maxWidth: 120 }}
        />
        {selectedCategory === categories[0] && (
          <UploadMedia
            type="video"
            src={src}
            handleMedia={(value) => handleChange("video", value)}
            handleTitle={(val) => handleChange("title", val)}
          />
        )}
        {selectedCategory === categories[1] && (
          <EmbedURL
            type="video"
            onConfirm={(value) => handleChange("video", value)}
          />
        )}
        {selectedCategory === categories[2] && (
          <MediaBrowser
            mediaType={"video"}
            onConfirm={(value) => handleChange("video", value)}
          />
        )}
        {selectedCategory === categories[3] && (
          <RecentMedia
            type={"video"}
            onClickMedia={(value) => handleChange("video", value)}
          />
        )}
      </>
    );
  };

  const renderVideoLayout = () => {
    return (
      <>
        <MarginBox
          margin={margin}
          increaseMargin={(side) => handleAdjustment(side, "increase")}
          decreaseMargin={(side) => handleAdjustment(side, "decrease")}
          isApplyMarginOnAllSides={isApplyMarginOnAllSides}
          allSideMargin={allSideMargin}
          handleMarginCheckBox={() => handleChange("marginCheckbox")}
          handleSliderMarginChange={(value) =>
            handleChange("allSideMargin", value)
          }
          handleMarginChange={(side, value) =>
            handleChange("margin", value, side)
          }
        />
        <SettingsSlider
          title="Resizing"
          sliderValue={percentageToSlider(width)}
          onValueChange={(val) => handleChange("width", val)}
          min={50}
          max={100}
          valueLabel={"%"}
        />
        <SettingsSlider
          title="Corner Rounding"
          sliderValue={borderRadius}
          onValueChange={(val) => handleChange("border", val)}
          min={0}
        />

        <ColorPopup
          title="Border"
          background={borderColor}
          handleChangeBackgroundColor={(val) =>
            handleChange("borderColor", val)
          }
          resetBackgroundColor={() => handleChange("resetColor")}
          sliderBoxTitle={"Border Width"}
          sliderValue={borderWidth}
          sliderChange={(val) => handleChange("borderWidth", val)}
          sliderMinValue={0}
          sliderMaxValue={8}
          isHideGradient={true}
          sliderSymbol={"px"}
          showSuggestedColorPalette={true}
          isBorderSlider
        />

        <ColorPopup
          title="Drop Shadow"
          background={dropShadowColor}
          handleChangeBackgroundColor={(val) =>
            handleChange("dropShadowColor", val)
          }
          resetBackgroundColor={() => handleChange("resetShadow")}
          showSlider={false}
          showSuggestedColorPalette={true}
          isHideGradient={true}
        />
      </>
    );
  };

  return (
    <>
      <Tabs
        categories={mainTabs}
        selectedCategory={selectedTab}
        onCategoryChange={(_, val) => setSelectedTab(val)}
        tabStyle={{ maxWidth: "50%" }}
        disabledCategories={src ? [] : [mainTabs[1]]}
      />
      <div style={{ marginBottom: 20 }} />
      {selectedTab === mainTabs[0] && renderVideoSelection()}
      {selectedTab === mainTabs[1] && renderVideoLayout()}
    </>
  );
};

export default VideoSettings;
